import React, { useRef } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';

// Vertically virtualized list. Renders ONLY the cards inside the viewport
// (plus a small overscan buffer) so giant orders (hundreds of cards) don't
// freeze the iPad. Used by Pressing Portal + Packing/QC Portal columns.
//
// Caller controls scroll-container styling via `className`/`style` props; this
// component wraps the children in a div that is the scrollElement.
//
// Props:
//   items         — array. Each entry is opaque to this component.
//   getKey(item)  — returns a stable string key per item.
//   renderItem(item, index) — returns the React node for that item.
//   estimateSize  — first-pass row height in px (will be measured for real
//                   on mount and the layout corrected). 200 is a sane default
//                   for the pressing/QC cards.
//   overscan      — how many off-screen rows to keep mounted. Default 5.
//   gap           — vertical gap between rows in px (mirrors flex gap).
//   emptyMessage  — optional node shown when items.length === 0.
//   className/style — applied to the scrolling wrapper.
//   scrollRef     — optional external ref to the scroll container (lets the
//                   parent imperatively scrollTo).

export default function VirtualCardList({
    items, getKey, renderItem,
    estimateSize = 200, overscan = 5, gap = 10,
    emptyMessage = null,
    className, style,
    scrollRef: scrollRefProp,
}) {
    const internalRef = useRef(null);
    const scrollRef   = scrollRefProp || internalRef;

    // estimateSize may be a scalar (all items same expected size) or a
    // function(index) → number (per-item estimate). Per-item is important
    // when different item kinds have very different heights — a bad initial
    // estimate leaves offsets stale and tiles can overlap.
    const estFn = typeof estimateSize === 'function'
        ? estimateSize
        : () => estimateSize;
    const virtualizer = useVirtualizer({
        count: items.length,
        getScrollElement: () => scrollRef.current,
        estimateSize: (index) => estFn(index),
        overscan,
        gap,
    });

    if (items.length === 0) {
        return (
            <div ref={scrollRef} className={className} style={style}>
                {emptyMessage}
            </div>
        );
    }

    const totalSize = virtualizer.getTotalSize();
    const virtualItems = virtualizer.getVirtualItems();

    return (
        <div ref={scrollRef} className={className} style={style}>
            <div
                style={{
                    height: `${totalSize}px`,
                    width: '100%',
                    position: 'relative',
                }}
            >
                {virtualItems.map(vi => {
                    const item = items[vi.index];
                    if (!item) return null;
                    return (
                        <div
                            key={getKey(item) || vi.index}
                            data-index={vi.index}
                            ref={virtualizer.measureElement}
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                transform: `translateY(${vi.start}px)`,
                            }}
                        >
                            {renderItem(item, vi.index)}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
